package screens.list

import common.EConstants
import common.ECssClasses
import components.ButtonContent
import components.Toolbar
import components.alert.Alert
import components.alert.AlertItem
import components.burgerMenu.BurgerMenu
import components.colourToneItem.EColourToneItemLeftButtonType
import components.items.ProductlistIcon
import components.items.ProductlistIconProps
import components.items.WatchlistIcon
import components.productItem.ProductItem
import components.toast.BottomToast
import components.toast.ToastItem
import core.AppContext
import core.enums.EImages
import core.enums.EJSBridgeActions
import core.enums.ELocalizationKeys
import core.extensions.isInNativeApp
import core.extensions.postMessageToNativeApp
import core.extensions.toClassName
import core.extensions.useCoroutineScope
import core.io.CustomHttpClient
import core.io.dto.ExternalLinkDTO
import core.io.dto.JSBridgeActionDTO
import core.ionic.IonButton
import core.ionic.IonCol
import core.ionic.IonContent
import core.ionic.IonGrid
import core.ionic.IonHeader
import core.ionic.IonPage
import core.ionic.IonRow
import core.localStorage.LocalStorageDAO
import core.localization.Localization
import core.piwik.Piwik
import csstype.Display
import csstype.JustifyContent
import csstype.NamedColor
import csstype.Overflow
import csstype.Position
import csstype.integer
import csstype.number
import csstype.pct
import csstype.px
import download
import emotion.react.css
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLDivElement
import react.FC
import react.Props
import react.dom.html.ReactHTML
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.router.Navigate
import react.router.useNavigate
import react.useContext
import react.useEffect
import react.useRef
import react.useState
import screens.EScreens

/**
 * Copyright (c) emgress GmbH. All rights reserved.
 *
 * Created on : 17.10.22
 * Author : mariorosshirt
 */

external interface ShowProductListScreenProps : Props {
}

val ShowProductListScreen = FC<ShowProductListScreenProps> { props ->

    val mContext by useContext(AppContext)
    val (mLocalStorageState, mLocalStorageDispatch) = mContext.mLocalStorageReducer

    var mTargetScreen: String? by useState(null)
    var mNavigate = useNavigate()

    var mToastItem: ToastItem? by useState(null)
    var mAlertItem: AlertItem? by useState(null)

    val mScope = useCoroutineScope()
    val mClient = CustomHttpClient()

    val mBottomNavButtonsRef = useRef<HTMLDivElement>(null)
    val mScrollViewSpacerRef = useRef<HTMLDivElement>(null)

    fun exportProductList() {
        if(!window.isInNativeApp()) {
            mScope.launch {
                val response = mClient.exportProductList(mLocalStorageState.mProductList)
                response.download(Localization.getLocalizationKey(ELocalizationKeys.EXPORT_FILENAME_PRODUCTLIST))
            }
        } else {
            val jsBridgeActionDTO = JSBridgeActionDTO(
                EJSBridgeActions.EXPORT_PRODUCTLIST,
                mLocalStorageState.mProductList
            )
            window.postMessageToNativeApp(jsBridgeActionDTO)
        }
    }

    fun shareProductList() {
        if (window.isInNativeApp()) {
            val jsBridgeActionDTO = JSBridgeActionDTO(
                EJSBridgeActions.SHARE_PRODUCTLIST,
                mLocalStorageState.mProductList
            )
            window.postMessageToNativeApp(jsBridgeActionDTO)
        }
    }

    useEffect(mBottomNavButtonsRef) {
        mScrollViewSpacerRef.current?.let {
            it.style.height = "${mBottomNavButtonsRef.current?.offsetHeight}px"
        }
    }

    BurgerMenu()
    IonPage {
        id = EConstants.BURGER_MENU_CONTENT.mId

        IonHeader {
            Toolbar {
                mEndViews = listOf(WatchlistIcon, FC<ProductlistIconProps>{ProductlistIcon{mIsActive = true}})
            }
            IonRow{
                IonCol{
                    css{
                        display = Display.flex
                        justifyContent = JustifyContent.spaceBetween}
                    span {
                        css(ECssClasses.LIST_HEADER_FONT.toClassName()) {}
                        +Localization.getLocalizationKey(ELocalizationKeys.S05_PRODUCT_LIST_HEADER.name)
                    }
                    div{
                        css{marginRight = 2.px}
                        if(window.isInNativeApp()){
                            IonButton {
                                fill = "clear"
                                ReactHTML.img {
                                    css {
                                        height = 24.px
                                        display = Display.flex
                                        justifyContent = JustifyContent.spaceBetween
                                    }
                                    src = EImages.SHARE_ICON(EImages.EColor.GRAY).mRelativePath
                                }
                                disabled = mLocalStorageState.mProductList.isEmpty()
                                onClick = {
                                    shareProductList()
                                    Piwik.trackCustomEvent(
                                        Piwik.CATEGORY_PRODUCT_LIST,
                                        Piwik.NAME_PRODUCTLIST_SHARE,
                                        Piwik.NAME_PRODUCTLIST_SHARE
                                    )
                                }
                            }
                        }
                        IonButton {
                            fill = "clear"
                            ReactHTML.img {
                                css {
                                    height = 24.px
                                }
                                src = EImages.DOWNLOAD_ICON(EImages.EColor.GRAY).mRelativePath

                            }
                            disabled = mLocalStorageState.mProductList.isEmpty()
                            onClick = {
                                exportProductList()
                                Piwik.trackCustomEvent(
                                    Piwik.CATEGORY_PRODUCT_LIST,
                                    Piwik.NAME_PRODUCTLIST_DOWNLOAD,
                                    Piwik.NAME_PRODUCTLIST_DOWNLOAD
                                )
                            }
                        }
                    }
                }
            }
        }
        IonContent {
            IonGrid {
                css(ECssClasses.VERTICAL_GRID.toClassName()) {}
                IonRow {
                    css() {
                        flexGrow = number(1.0)
                        overflow = Overflow.scroll
                    }
                    IonCol {
                        size = "12"
                        mLocalStorageState.mProductList.forEach {
                            ProductItem() {
                                mColourObject = it
                                mColourToneItemLeftButtonToastMessage = { toastItem ->
                                    mToastItem = toastItem
                                }
                                mDeleteFunction = {
                                    mAlertItem = AlertItem.confirmDeleteAlertItem(
                                        Localization.getLocalizationKey(
                                            ELocalizationKeys.S05_DELETE_CONFIRMATION_TEXT
                                        )
                                    ) {
                                        mLocalStorageDispatch(
                                            LocalStorageDAO.ELocalStorageAction.DELETE_FROM_PRODUCTLIST(
                                                it
                                            )
                                        ).also {
                                            mToastItem = ToastItem(
                                                Localization.getLocalizationKey(
                                                    ELocalizationKeys.CORE_TOAST_DELETE_FROM_PRODUCT_LIST
                                                )
                                            )
                                        }                                    }
                                }
                                mColourToneItemLeftButtonType = EColourToneItemLeftButtonType.WATCH_LIST
                                mEmptyEanFunction = {mAlertItem = AlertItem.okAlertItem(
                                    Localization.getLocalizationKey(
                                        ELocalizationKeys.S05_NO_EAN_ALERT_TEXT
                                    )
                                ){}}
                            }
                        }

                        if(mLocalStorageState.mProductList.isNotEmpty()) {
                            div {
                                css(ECssClasses.LIST_NOTE_STYLE.toClassName()) {}
                                div {
                                    css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT_WEIGHTED.toClassName()) {

                                    }
                                    +Localization.getLocalizationKey(ELocalizationKeys.S05_PRODUCT_LIST_HINT_TITLE.name)
                                }
                                div {
                                    css {
                                        marginTop = 8.px
                                    }
                                    +Localization.getLocalizationKey(ELocalizationKeys.S05_PRODUCT_LIST_HINT_TEXT.name)
                                }
                            }
                        } else {
                            div {
                                css(ECssClasses.LIST_NOTE_STYLE.toClassName()) {}
                                div {
                                    css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT_WEIGHTED.toClassName()) {

                                    }
                                    +Localization.getLocalizationKey(ELocalizationKeys.
                                    S05_PRODUCT_LIST_EMPTY_TITLE.name)
                                }
                                div {
                                    css {
                                        marginTop = 8.px
                                    }
                                    +Localization.getLocalizationKey(ELocalizationKeys.S05_PRODUCT_LIST_EMPTY_TEXT.name)
                                }
                                div {
                                    css {
                                        display = Display.flex
                                        justifyContent = JustifyContent.center
                                    }

                                    IonButton {
                                        css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                            width = 165.px
                                            marginTop = 42.px
                                        }
                                        onClick = {
                                            mTargetScreen = EScreens.COLOUR_FINDER_UPLOAD_SCREEN.mPath
                                        }
                                        ButtonContent {
                                            mContent =
                                                Localization.getLocalizationKey(ELocalizationKeys.
                                                S05_PRODUCT_LIST_EMPTY_BUTTON_CONTENT.name)
                                        }
                                    }
                                }
                            }
                        }

                        div {
                            ref = mScrollViewSpacerRef
                        }
                    }

                    mToastItem?.let {
                        BottomToast {
                            this.mToastItem = it
                        }
                    }
                }
                /*
                div {
                    css() {
                        backgroundColor = NamedColor.white
                        width = 100.pct
                        zIndex = integer(200)
                        visibility = Visibility.hidden
                    }
                    div {
                        css() {
                            justifyContent = JustifyContent.spaceBetween
                            display = Display.flex
                            padding = 0.px
                            marginTop = 28.px
                            marginBottom = 33.px
                            marginRight = 30.px
                            marginLeft = 20.px
                        }
                        IonButton {
                            css(ECssClasses.SECONDARY_BUTTON.toClassName()) {
                                width = 165.px
                            }
                            onClick = {
                                mNavigate.invoke(-1)
                            }
                            ButtonContent {
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.CORE_BACK.name)
                            }
                        }
                        IonButton {
                            css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                width = 165.px
                            }
                            onClick = {
                                Piwik.trackCustomEvent(
                                    Piwik.CATEGORY_SEARCH,
                                    Piwik.NAME_SEARCH_OPEN_RETAILER,
                                    Piwik.NAME_SEARCH_OPEN_RETAILER
                                )
                                if(window.isInNativeApp()){
                                    window.open(Localization.getLocalizationKey
                                        (ELocalizationKeys.RETAIL_SEARCH_URL.name))
                                }else{
                                    val jsBridgeActionDTO = JSBridgeActionDTO(
                                        EJSBridgeActions.EXTERNAL_LINK,
                                        ExternalLinkDTO(Localization.getLocalizationKey
                                            (ELocalizationKeys.RETAIL_SEARCH_URL.name))
                                    )
                                    window.postMessageToNativeApp(jsBridgeActionDTO)
                                }
                            }
                            ButtonContent {
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.RETAIL_BUTTON_TITLE.name)
                            }
                        }
                    }
                }

                 */

                div {
                    ref = mBottomNavButtonsRef
                    css() {
                        position = Position.fixed
                        bottom = 0.px
                        left = 0.px
                        backgroundColor = NamedColor.white
                        width = 100.pct
                        zIndex = integer(200)
                    }
                    div {
                        css {
                            justifyContent = JustifyContent.spaceBetween
                            display = Display.flex
                            padding = 0.px
                            marginTop = 28.px
                            marginBottom = 33.px
                            marginRight = 25.px
                            marginLeft = 25.px
                        }
                        IonButton {
                            css(ECssClasses.SECONDARY_BUTTON.toClassName()) {
                                width = 165.px
                            }
                            onClick = {
                                mNavigate.invoke(-1)
                            }
                            ButtonContent {
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.CORE_BACK.name)
                            }
                        }
                        IonButton {
                            css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                width = 165.px
                            }
                            onClick = {

                                Piwik.trackCustomEvent(
                                    Piwik.CATEGORY_SEARCH,
                                    Piwik.NAME_SEARCH_OPEN_RETAILER,
                                    Piwik.NAME_SEARCH_OPEN_RETAILER
                                )
                                if(!window.isInNativeApp()){
                                    window.open(Localization.getLocalizationKey
                                        (ELocalizationKeys.RETAIL_SEARCH_URL.name))
                                }else{
                                    val jsBridgeActionDTO = JSBridgeActionDTO(
                                        EJSBridgeActions.EXTERNAL_LINK,
                                        ExternalLinkDTO(Localization.getLocalizationKey
                                            (ELocalizationKeys.RETAIL_SEARCH_URL.name))
                                    )
                                    window.postMessageToNativeApp(jsBridgeActionDTO)
                                }
                            }
                            ButtonContent {
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.RETAIL_BUTTON_TITLE.name)
                            }
                        }
                    }
                }
            }
        }
    }
    mTargetScreen?.let {
        Navigate {
            to = it
        }
    }
    mAlertItem?.let {
        Alert {
            this.mAlertItem = it
        }
    }
}